import { useState } from 'react';

import { RiShieldCheckLine } from '@remixicon/react';

import { Tag, Text } from '@spotify-internal/encore-web';
import { ConversationsV2ToolConfirmationResponseDecision } from '@spotify/confidence-graphql';

import { useToolExtensions } from '../../extensions';
import { useConfirmTool } from '../../hooks/useConfirmTool';
import { renderInlineMarkdownLinks } from '../../utils/inlineMarkdownLinks';
import {
  ConfirmationConfigGrid,
  ResourceConfirmationCard,
} from '../ResourceConfirmationCard';
import { ToolIcon as ToolEntryIcon } from '../ToolCallGroup/ToolIcon';
import {
  formatDefaultToolLabel,
  inferToolIcon,
} from '../ToolCallGroup/defaultToolMappings';
import {
  getDefaultPreviewFields,
  isPlainObject,
  resolveInputBody,
} from './inputUtils';

import styles from './ToolConfirmationMessage.module.css';

export const ToolConfirmationRequestMessage = ({
  eventName,
  tool,
  displayName,
  input,
}: {
  eventName: string;
  tool: string;
  displayName?: string | null;
  input?: unknown;
}) => {
  const toolExtensions = useToolExtensions();
  const extension = toolExtensions.find(e => e.predicate(tool));
  const entry = { tool, displayName, input };
  const label = extension?.formatLabel
    ? extension.formatLabel(entry)
    : formatDefaultToolLabel(entry);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [pendingDecision, setPendingDecision] =
    useState<ConversationsV2ToolConfirmationResponseDecision | null>(null);
  const { confirmTool, isMutating, error } = useConfirmTool({
    onComplete: () => setHasSubmitted(true),
  });

  const handleDecide = (
    decision: ConversationsV2ToolConfirmationResponseDecision,
  ) => {
    setPendingDecision(decision);
    void confirmTool(eventName, decision).catch(() => {
      setPendingDecision(null);
    });
  };

  const actionsDisabled = isMutating || hasSubmitted;

  const confirmationProps = {
    input,
    onDecide: handleDecide,
    actionsDisabled,
    pendingDecision,
    error: error ?? null,
  };

  if (extension?.renderConfirmation) {
    return extension.renderConfirmation(confirmationProps);
  }

  const title =
    extension?.confirmationTitle ??
    (label.detail ? (
      <>
        {label.text} — {label.detail}
      </>
    ) : (
      label.text
    ));
  const parsedInput = isPlainObject(input) ? resolveInputBody(input) : {};
  const previewFields =
    extension?.previewFields ?? getDefaultPreviewFields(parsedInput);

  return (
    <ResourceConfirmationCard
      icon={
        <ToolEntryIcon
          entry={entry}
          extension={extension}
          size={16}
          isStreaming={false}
        />
      }
      title={title}
      {...confirmationProps}
    >
      {previewFields.length > 0 && (
        <ConfirmationConfigGrid
          input={parsedInput}
          previewFields={previewFields}
        />
      )}
    </ResourceConfirmationCard>
  );
};

export const ToolConfirmationResponseMessage = ({
  decision,
  tool,
  displayName,
}: {
  decision: ConversationsV2ToolConfirmationResponseDecision;
  tool?: string;
  displayName?: string | null;
}) => {
  const approved =
    decision !== ConversationsV2ToolConfirmationResponseDecision.Deny;
  const ToolIcon = tool ? inferToolIcon(tool) : RiShieldCheckLine;
  const statusLabel = approved ? 'Approved' : 'Cancelled';
  const label = tool ? (displayName ?? tool.replace(/_/g, ' ')) : statusLabel;

  return (
    <div className={styles.responseCard}>
      <div className={styles.responseContent}>
        <span className={styles.responseIcon}>
          <ToolIcon size={16} />
        </span>
        <Text variant="bodySmall">{renderInlineMarkdownLinks(label)}</Text>
      </div>
      {tool && (
        <Tag colorSet={approved ? 'positive' : undefined}>{statusLabel}</Tag>
      )}
    </div>
  );
};
